import React from 'react';
import { Alert, Checkbox, FormControlLabel, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import CaseIntakeApi, { IntakePaymentDefaults } from '../../api/CaseIntakeApi';
import { paymentBoxDecision, type PaymentOnCreateChoice } from '../../utils/paymentOnCreate';

/**
 * "Send payment link by email and text", on the screens that create a case: Create case and the New
 * Ticket wizard (Lloyd, Oct 2026: "it takes 5 steps to send a payment link. This should be down to
 * 1-2"). With it ticked, the one press that creates the case also sends the link
 * (utils/paymentOnCreate).
 *
 * How it opens is worked out on the server by the payment step's own rules
 * (POST /case-intake/payment-defaults): ticked for a client who pays the firm directly, unticked for
 * a referral partner's, refused for OTR. Staff can change the box; the amount follows the plan's price
 * until they type their own. Text still needs the client's consent.
 */

/** Who the case is for, as far as the screen knows it. Null while no client is chosen. */
export type PaymentLinkBoxClient =
  | { User_guid: string; markOtr: boolean; plan?: string }
  | { source?: string; email?: string; phone?: string; markOtr: boolean; plan?: string };

export interface PaymentLinkBox {
  defaults: IntakePaymentDefaults | null;
  /** Whether Create will send the link. */
  willSend: boolean;
  /** Why Create cannot go ahead yet, or null. */
  problem: string | null;
  /** What to pass to sendPaymentAfterCreate once the case exists. */
  choice: (created: { emailNotSaved?: boolean }) => PaymentOnCreateChoice;
  /** For <SendPaymentLinkBox>. */
  view: SendPaymentLinkBoxProps;
}

export function usePaymentLinkBox(client: PaymentLinkBoxClient | null): PaymentLinkBox {
  const [defaults, setDefaults] = React.useState<IntakePaymentDefaults | null>(null);
  const [send, setSend] = React.useState(false);
  const [sendTouched, setSendTouched] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const [amountTouched, setAmountTouched] = React.useState(false);

  // One key for what the defaults depend on, so a new object each render does not refetch.
  const key = client ? JSON.stringify(client) : null;
  React.useEffect(() => {
    if (!key) {
      setDefaults(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      CaseIntakeApi.paymentDefaults(JSON.parse(key))
        .then((result) => {
          if (cancelled) return;
          setDefaults(result);
          if (!sendTouched) setSend(result.mode === 'ready' && (result.email || result.sms));
          if (!amountTouched) setAmount(result.planCents ? (result.planCents / 100).toFixed(2) : '');
        })
        .catch(() => {
          if (!cancelled) setDefaults(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [key, sendTouched, amountTouched]);

  const box = { send, amount, plan: client?.plan || undefined };
  const { willSend, problem } = paymentBoxDecision(defaults, box);
  const dollars = Number(amount);
  const amountValid = Number.isFinite(dollars) && dollars >= 0.5 && dollars <= 50000;

  return {
    defaults,
    willSend,
    problem,
    choice: (created) => paymentBoxDecision(defaults, box, created).choice,
    view: {
      defaults,
      checked: willSend,
      onCheck: (checked) => {
        setSendTouched(true);
        setSend(checked);
      },
      amount,
      amountValid,
      amountFromPlan: Boolean(defaults?.planCents) && !amountTouched,
      onAmount: (value) => {
        setAmountTouched(true);
        setAmount(value);
      },
    },
  };
}

export interface SendPaymentLinkBoxProps {
  defaults: IntakePaymentDefaults | null;
  checked: boolean;
  onCheck: (checked: boolean) => void;
  amount: string;
  amountValid: boolean;
  amountFromPlan: boolean;
  onAmount: (value: string) => void;
}

const SendPaymentLinkBox: React.FC<SendPaymentLinkBoxProps> = ({
  defaults,
  checked,
  onCheck,
  amount,
  amountValid,
  amountFromPlan,
  onAmount,
}) => {
  if (!defaults) return null;
  const blocked = defaults.mode === 'blocked';
  const email = !defaults.emailUnavailable;
  const sms = !defaults.smsUnavailable;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            disabled={blocked || (!email && !sms)}
            onChange={(event) => onCheck(event.target.checked)}
          />
        }
        label="Send payment link by email and text"
      />
      {defaults.note && (
        <Alert severity={blocked ? 'info' : 'warning'} sx={{ mt: 1 }}>
          {defaults.note}
        </Alert>
      )}
      {!blocked && !email && !sms && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No email or texts for this client yet, so no link can be sent. Add one, or send it later from the
          Payments tab.
        </Typography>
      )}
      {checked && (
        <Stack spacing={1} sx={{ mt: 1, ml: 4 }}>
          <TextField
            label="Amount"
            size="small"
            value={amount}
            onChange={(event) => onAmount(event.target.value)}
            error={amount !== '' && !amountValid}
            helperText={
              amountFromPlan
                ? 'The price of the plan. Change it if the client owes something else.'
                : 'Enter what the client owes.'
            }
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ maxWidth: 240 }}
          />
          <Typography variant="body2">
            {email ? `Email to ${defaults.emailAddress}.` : `No email: ${defaults.emailUnavailable}.`}{' '}
            {sms ? `Text to ${defaults.phoneNumber}.` : `No text: ${defaults.smsUnavailable}.`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Sent when you press Create: the firm&apos;s payment email and text, with the note on how to pay
            (card or Klarna through Stripe, or another way by calling the office).
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

export default SendPaymentLinkBox;
